import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import * as DataAccessSearchActions from './data-access-search.actions';

import { SearchResult, SearchResultsCounts } from './data-access-search.models';

import { SearchStringFilter } from '../../model/search-filter';
import { SearchAutocompleteSuggestion } from '../../service/search-api-response.models';

export const DATA_ACCESS_SEARCH_RESULTS_FEATURE_KEY = 'dataAccessSearchResults';

export interface State extends EntityState<SearchResult> {
  counts?: SearchResultsCounts;
  total_counts?: SearchResultsCounts;
  loaded: boolean;
  error?: string | null;
  autocompleteSuggestions?: SearchAutocompleteSuggestion[];
  searchFilterModalOpen: boolean;
  stringFilters?: SearchStringFilter[];
  suggestedSearchTerm?: string;
  resultSuggestions?: SearchResult[];
}

export interface DataAccessSearchPartialState {
  readonly [DATA_ACCESS_SEARCH_RESULTS_FEATURE_KEY]: State;
}

export function selectSearchResultId(searchResultItem: SearchResult) {
  return `${searchResultItem.type}/${searchResultItem.id}`;
}

export const dataAccessSearchAdapter: EntityAdapter<SearchResult> = createEntityAdapter<SearchResult>({
  selectId: selectSearchResultId,
});

export const initialState: State = dataAccessSearchAdapter.getInitialState({
  loaded: false,
  searchFilterModalOpen: false,
});

const dataAccessSearchResultsReducer = createReducer(
  initialState,
  on(DataAccessSearchActions.searchPageOpened, (state) =>
    dataAccessSearchAdapter.setAll([], {
      ...state,
      suggestedSearchTerm: undefined,
      resultSuggestions: undefined,
      counts: undefined,
      loaded: false,
      error: null,
    })
  ),
  on(DataAccessSearchActions.loadSearchResults, (state) => ({
    ...state,
    loaded: false,
  })),
  on(DataAccessSearchActions.searchResultsLoadSucceeded, (state, { counts, results, requestedFilters }) =>
    dataAccessSearchAdapter.setAll(results, {
      ...state,
      counts: counts,
      stringFilters: requestedFilters.stringFilter,
      loaded: true,
      error: null,
    })
  ),
  on(DataAccessSearchActions.loadSearchTermSuggestions, (state) => ({
    ...state,
    loaded: false,
  })),
  on(DataAccessSearchActions.searchResultsLoadFailed, (state, { error }) =>
    dataAccessSearchAdapter.setAll([], { ...state, counts: undefined, loaded: true, error })
  ),
  on(DataAccessSearchActions.searchFilterModalOpened, (state) => ({ ...state, searchFilterModalOpen: true })),
  on(DataAccessSearchActions.searchFilterModalClosed, (state) => ({ ...state, searchFilterModalOpen: false })),
  on(DataAccessSearchActions.searchTermAutocompleteLoadSucceeded, (state, { suggestions }) => ({
    ...state,
    autocompleteSuggestions: suggestions,
  })),
  on(DataAccessSearchActions.searchFormValueIsInvalid, (state) => ({
    ...state,
    autocompleteSuggestions: [],
  })),
  on(DataAccessSearchActions.searchFormValueChanged, (state, { searchTermFragment }) => ({
    ...state,
    autocompleteSuggestions: state.autocompleteSuggestions?.filter((item) =>
      item.suggestion.includes(searchTermFragment ? searchTermFragment : '')
    ),
  })),
  on(DataAccessSearchActions.useSuggestedSearchTerm, (state, { suggestedSearchTerm }) => ({
    ...state,
    suggestedSearchTerm,
  })),
  on(DataAccessSearchActions.searchResultSuggestionsLoadSucceeded, (state, { resultSuggestions }) => ({
    ...state,
    loaded: true,
    resultSuggestions: resultSuggestions,
  })),
  on(DataAccessSearchActions.searchCountsLoadSucceeded, (state, { counts }) => ({
    ...state,
    total_counts: counts,
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return dataAccessSearchResultsReducer(state, action);
}
